import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@material-ui/core';
import {
  Edit as EditIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  signatureDisplay: {
    width: 568,
    minHeight: 90,
    border: '1px solid #ccc',
    borderRadius: theme.shape.borderRadius,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: theme.palette.grey[50],
    position: 'relative',
    overflow: 'hidden',
    '&:hover': {
      backgroundColor: theme.palette.grey[100],
    },
  },
  signatureImage: {
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'contain',
  },
  placeholderText: {
    color: theme.palette.text.secondary,
    fontSize: '0.875rem',
    textAlign: 'center',
  },
  signatureCanvas: {
    border: '1px solid #ccc',
    borderRadius: theme.shape.borderRadius,
    cursor: 'crosshair',
    backgroundColor: 'white',
  },
  dialogActions: {
    padding: theme.spacing(1, 3, 2),
    display: 'flex',
    justifyContent: 'space-between',
  },
  leftActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  rightActions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
}));

interface SignatureInputProps {
  value?: string;
  onChange: (signature: string | null) => void;
  label?: string;
  disabled?: boolean;
  required?: boolean;
  width?: number;
  height?: number;
}

const SignatureInput: React.FC<SignatureInputProps> = ({
  value,
  onChange,
  label = 'Signature',
  disabled = false,
  required = false,
  width = 568,
  height = 90,
}) => {
  const classes = useStyles();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    setHasSignature(!!value);
  }, [value]);

  const openSignatureDialog = () => {
    if (!disabled) {
      setDialogOpen(true);
      // Reset canvas when opening
      setTimeout(() => {
        clearCanvas();
      }, 100);
    }
  };

  const closeSignatureDialog = () => {
    setDialogOpen(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        setHasSignature(false);
      }
    }
  };

  const saveSignature = () => {
    const canvas = canvasRef.current;
    if (canvas) {
      // Check if canvas has content
      const ctx = canvas.getContext('2d');
      if (ctx) {
        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
        const pixels = imageData.data;
        let hasContent = false;

        // Check if any pixel is not white (rgba(255,255,255,255))
        for (let i = 0; i < pixels.length; i += 4) {
          if (pixels[i] !== 255 || pixels[i + 1] !== 255 || pixels[i + 2] !== 255 || pixels[i + 3] !== 255) {
            hasContent = true;
            break;
          }
        }

        if (hasContent) {
          const dataURL = canvas.toDataURL('image/png');
          onChange(dataURL);
          setHasSignature(true);
        } else {
          onChange(null);
          setHasSignature(false);
        }
      }
    }
    closeSignatureDialog();
  };

  const clearSignature = () => {
    onChange(null);
    setHasSignature(false);
  };

  // Mouse events
  const startDrawing = (e: React.MouseEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo(x, y);
        setIsDrawing(true);
      }
    }
  };

  const draw = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;

    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.lineTo(x, y);
        ctx.stroke();
      }
    }
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  // Touch events for mobile
  const startDrawingTouch = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    const canvas = canvasRef.current;
    if (canvas && e.touches.length === 1) {
      const rect = canvas.getBoundingClientRect();
      const x = e.touches[0].clientX - rect.left;
      const y = e.touches[0].clientY - rect.top;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.beginPath();
        ctx.moveTo(x, y);
        setIsDrawing(true);
      }
    }
  };

  const drawTouch = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    if (!isDrawing || e.touches.length !== 1) return;

    const canvas = canvasRef.current;
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      const x = e.touches[0].clientX - rect.left;
      const y = e.touches[0].clientY - rect.top;

      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.lineTo(x, y);
        ctx.stroke();
      }
    }
  };

  const stopDrawingTouch = (e: React.TouchEvent<HTMLCanvasElement>) => {
    e.preventDefault();
    setIsDrawing(false);
  };

  // Initialize canvas
  useEffect(() => {
    if (dialogOpen && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        // Set up drawing style
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        // Fill with white background
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, [dialogOpen]);

  return (
    <Box>
      {label && (
        <Typography variant="body2" style={{ marginBottom: 8 }}>
          {label}
          {required && <span style={{ color: 'red' }}> *</span>}
        </Typography>
      )}
      
      <Box
        className={classes.signatureDisplay}
        onClick={openSignatureDialog}
        style={{ width, height }}
      >
        {value ? (
          <img
            src={value}
            alt="Signature"
            className={classes.signatureImage}
          />
        ) : (
          <Typography className={classes.placeholderText}>
            <EditIcon style={{ fontSize: 16, marginRight: 4 }} />
            Click to add signature
          </Typography>
        )}
      </Box>

      {value && (
        <Button
          size="small"
          onClick={clearSignature}
          startIcon={<ClearIcon />}
          style={{ marginTop: 8 }}
          disabled={disabled}
        >
          Clear Signature
        </Button>
      )}

      <Dialog
        open={dialogOpen}
        onClose={closeSignatureDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{label}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Draw your signature in the area below:
          </Typography>
          <canvas
            ref={canvasRef}
            width={width}
            height={height * 2} // Make it taller for better signing experience
            className={classes.signatureCanvas}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            onTouchStart={startDrawingTouch}
            onTouchMove={drawTouch}
            onTouchEnd={stopDrawingTouch}
          />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Box className={classes.leftActions}>
            <Button
              onClick={clearCanvas}
              startIcon={<ClearIcon />}
              variant="outlined"
            >
              Reset
            </Button>
          </Box>
          <Box className={classes.rightActions}>
            <Button onClick={closeSignatureDialog} startIcon={<CancelIcon />}>
              Cancel
            </Button>
            <Button
              onClick={saveSignature}
              startIcon={<SaveIcon />}
              variant="contained"
              color="primary"
            >
              Add Signature
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SignatureInput; 